import { ActionIcon, AppShell, Box, NavLink, ScrollArea } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload
} from "@tabler/icons-react";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

type NavItem = {
  label: string;
  to: string;
  icon: React.ElementType;
};

type SidebarProps = {
  collapsed: boolean;
  navItems?: NavItem[];
  onToggleCollapse: () => void;
};

export default function Sidebar({
  collapsed,
  navItems,
  onToggleCollapse
}: SidebarProps) {
  const matchRoute = useMatchRoute();
  const { canInstall, install } = useInstallPrompt();

  return (
    <Box
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <ActionIcon
        onClick={onToggleCollapse}
        variant="transparent"
        size="md"
        radius="xl"
        style={{
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 200,
          color: "white"
        }}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <IconChevronRight size={25} stroke={2.5} />
        ) : (
          <IconChevronLeft size={25} stroke={2.5} />
        )}
      </ActionIcon>

      <AppShell.Section grow component={ScrollArea}>
        <Box component="nav">
          {navItems?.map(({ label, to, icon: Icon }, index) => {
            const active = !!matchRoute({ to, fuzzy: true });
            const isLast = index === navItems.length - 1;
            return (
              <NavLink
                key={to}
                component={Link}
                to={to}
                label={collapsed ? "" : label}
                leftSection={<Icon size={20} stroke={1.75} />}
                py={16}
                pl={20}
                active={active}
                variant="subtle"
                color="sparkYellow.12"
                c={active ? undefined : "white"}
                fw={active ? 600 : 500}
                mod={{ inactive: !active }}
                style={
                  isLast
                    ? undefined
                    : { borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }
                }
              />
            );
          })}
        </Box>
      </AppShell.Section>

      {canInstall && (
        <AppShell.Section style={{ marginTop: "auto" }}>
          <NavLink
            label={collapsed ? "" : "Install App"}
            leftSection={<IconDownload size={20} stroke={1.75} />}
            py={16}
            pl={20}
            active={false}
            variant="subtle"
            c="white"
            fw={500}
            mod={{ inactive: true }}
            onClick={() => void install()}
            style={{ borderTop: "1px solid rgba(255, 255, 255, 0.2)" }}
          />
        </AppShell.Section>
      )}
    </Box>
  );
}
